import React, { useContext, useState } from 'react';
import { View } from 'react-native';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import styled from 'styled-components/native';

import ClientContext from 'app/src/contexts/ClientContext';
import { Loading } from 'app/src/elements/DataState';

import { Button } from 'app/src/elements/buttons';
import { Fields, defaultValues, EMAIL_PATTERN } from 'app/src/elements/forms';
import { Notice } from 'app/src/elements/Notice';
import { Title, Text } from 'app/src/styles';

import formatAuthErrors from './formatAuthErrors';
import { ActionLink, ActionLabel } from './ActionLink';
import FormContainer from './FormContainer';
import {
  USER_STATUS,
  REGISTER_USER,
  LOGIN,
  SEND_PASSWORD_RESET,
} from './queries';

const REGISTER_FIELDS = {
  email: {
    rules: {
      required: true,
      pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email' },
    },
    inputProps: { placeholder: 'your.email@example.com' }
  },
  name: {
    label: 'Your Name',
    rules: { required: true },
    inputProps: { autoFocus: true }
  },
};

const FIELDS = {
  ...REGISTER_FIELDS,
  password: {
    rules: { required: true },
    inputProps: {
      secureTextEntry: true,
      placeholder: 'Password',
      autoFocus: true
    }
  },
};

const LOGIN_FIELDS = {
  email: FIELDS.email,
  password: FIELDS.password,
};

const returnToHome = () => window.location.href = '/';

const RegisterLogin = ({ registrationCode }) => {
  const [formStatus, setFormStatus] = useState();
  const [errorMessage, setErrorMessage] = useState();

  const { updateCredentials } = useContext(ClientContext);

  const { control, handleSubmit, watch, reset, formState } = useForm({
    defaultValues: defaultValues({ fields: FIELDS })
  });

  const watchEmail = watch('email');

  const { errors } = formState;

  const [loadUserStatus, { data }] = useLazyQuery(USER_STATUS);

  const [login] = useMutation(LOGIN, {
    onCompleted: data => {
      updateCredentials(data.userLogin.credentials);
      returnToHome();
    },
    onError: error => {
      setErrorMessage(formatAuthErrors(error.graphQLErrors));
      setFormStatus(null);
    }
  });

  const [registerUser] = useMutation(REGISTER_USER, {
    onCompleted: data => {
      if (data.registerUser.user) {
        setFormStatus('success');
      }
    }
  });

  const [sendPasswordReset] = useMutation(SEND_PASSWORD_RESET, {
    onCompleted: () => setFormStatus('reset-sent')
  });

  const onSubmit = input => {
    setFormStatus('submitting');
    setErrorMessage(null);
    registerUser({
      variables: { input: { email: input.email, name: input.name } }
    });
  };

  const onCheckStatus = input => {
    loadUserStatus({ variables: { email: input.email }});
  };

  const onLogin = input => {
    setFormStatus('submitting');
    setErrorMessage(null);
    login({
      variables: {
        email: input.email,
        password: input.password,
      }
    });
  };

  const onPasswordReset = input => {
    sendPasswordReset({
      variables: {
        email: watchEmail,
        redirectUrl: window.location.origin,
      }
    });
  };

  let content = (
    <FormContainer onEnter={handleSubmit(onCheckStatus)}>
      <Title>Login or Register</Title>
      <Fields
        fields={{ email: REGISTER_FIELDS.email }}
        control={control}
        errors={errors}
      />
      <Button label="Next" onPress={handleSubmit(onCheckStatus)} wide />
    </FormContainer>
  );

  if ('success' === formStatus) {
    content = (
      <Notice type="success">
        Check your email for next steps.
      </Notice>
    );
  } else if ('reset-sent' === formStatus) {
    content = (
      <Notice type="success">
        Check your email for a link to reset your password.
      </Notice>
    );
  } else if ('submitting' === formStatus) {
    content = <Loading />;
  } else if ('available' === data?.userStatus) {
    content = (
      <FormContainer onEnter={handleSubmit(onSubmit)}>
        <Title>Register</Title>
        <Fields fields={REGISTER_FIELDS} control={control} errors={errors} />
        <Button label="Register" onPress={handleSubmit(onSubmit)} wide />

        <ActionLink onPress={returnToHome}>
          <ActionLabel>Back to Login</ActionLabel>
        </ActionLink>
      </FormContainer>
    )
  } else if ('invited' === data?.userStatus) {
    content = (
      <>
        <Notice type="warning">
          Your email has not been confirmed.
        </Notice>
        <Button label="Resend Email" onPress={onPasswordReset} wide />
      </>
    );
  } else if ('confirmed' === data?.userStatus) {
    content = (
      <>
        <FormContainer onEnter={handleSubmit(onLogin)}>
          <Title>Login</Title>
          {errorMessage && <Notice type="error">{errorMessage}</Notice>}
          <Fields fields={LOGIN_FIELDS} control={control} errors={errors} />
          <Button label="Login" onPress={handleSubmit(onLogin)} wide />
        </FormContainer>

        <ActionLink onPress={onPasswordReset}>
          <ActionLabel>Send Password Reset Email</ActionLabel>
        </ActionLink>
      </>
    );
  }

  return (
    <View>
      {content}
    </View>
  );
};

export default RegisterLogin;
